import React, { Component } from 'react'
import { setDragging } from "template-edit/reducers"
import { connect } from "react-redux"
import * as d3 from "d3"

import { AlternativeTemplatesLevels, CollageLayoutType, Template } from "app/types/model-types"
import { RandomProduct } from "shape-edit/types"

import { setActiveShapeId, setActiveView } from "shape-edit/reducers";
import { DEFAULT_CURSOR, DeltaPositionsType, MOVE_CURSOR, OVERLAY_COLOR_NEGATIVE, OVERLAY_COLOR_POSITIVE } from "template-edit/containers/image-preview";
import { ActiveViewType } from "template-edit/containers/Editor";
import SourceImageSvgElements from "./source-image-svg-elements";
import {  getFormName, getTemplateSize } from 'shape-edit/forms/helpers'
import { initForm, selectFormValues } from 'app/reducers/form'
import { ReduxList } from 'app/types/app-types'
import { LevelsModeType } from 'template-edit/components/save-template-form'
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers'

const { min, max, abs, sin, cos, PI, round, atan2, ceil } = Math

interface Props {
    levels: string[]
    levelsMode: LevelsModeType
    alternativeTemplatesLevels: ReduxList<AlternativeTemplatesLevels>
    randomProduct: RandomProduct
    collageLayoutsForm: CollageLayoutType[]
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    activeView: ActiveViewType
    setDragging: typeof setDragging
    initForm: typeof initForm
    setActiveView: typeof setActiveView
    setActiveShapeId: typeof setActiveShapeId
    showOverlap: boolean
    isDraggable: boolean
    deltaAngle?: number
    deltaPositions?: DeltaPositionsType
    setDeltaAngle?: Function
    setDeltaPositions?: Function
}

interface State {
    x: number
    y: number
    shapeX: number
    shapeY: number
    dragStartX: number
    dragStartY: number
    isLoading: boolean
}

class SourceImageSvg extends Component<Props, State> {
    groupRef: React.RefObject<SVGGElement>

    constructor(props) {
        super(props)

        this.state = {
            x: 0,
            y: 0,
            shapeX: 0,
            shapeY: 0,
            dragStartX: 0,
            dragStartY: 0,
            isLoading: false,
        }

        this.groupRef = React.createRef()
    }

    dragStarted = (event) => {
        if (this.props.isDraggable) {
            const el = this.groupRef.current
            if (el) {
                this.props.setDragging(true);
                d3.select(el).classed("active", true);
                d3.select(el).style("cursor", MOVE_CURSOR);

                this.setState({
                    shapeX: this.state.x,
                    shapeY: this.state.y,
                    dragStartX: event.x,
                    dragStartY: event.y,
                })
            }
        }
    }

    dragged = (event) => {
        const { isDraggable } = this.props
        if (isDraggable) {
            this.setState({
                x: this.state.shapeX + event.x - this.state.dragStartX,
                y: this.state.shapeY + event.y - this.state.dragStartY,
            })
        }
    }

    dragEnded = (event) => {
        const { form, isAlt } = this.props

        const formName = getFormName(isAlt)

        const { isDraggable } = this.props
        if (isDraggable) {
            const el = this.groupRef.current
            if (el) {
                this.props.initForm(formName, {
                    ...form,
                    translate_x: form.translate_x + round(this.state.x),
                    translate_y: form.translate_y + round(this.state.y),
                })
                this.setState({ x: 0, y: 0 })
                this.props.setDragging(false);
                d3.select(el).classed("active", false)
            }
        }
    }

    bindEvents = () => {
        const el = this.groupRef.current
        if (el && d3.select(el)) {
            d3.select(el)
                .style("cursor", this.props.isDraggable ? MOVE_CURSOR : DEFAULT_CURSOR)
                .call(d3.drag()
                    .on("start", (event) => this.dragStarted(event))
                    .on("drag", (event) => this.dragged(event))
                    .on("end", (event) => this.dragEnded(event))
                )
        }
    }

    componentDidMount() {
        this.bindEvents()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && (this.props.isDraggable !== prevProps.isDraggable || this.props.activeView !== prevProps.activeView)) {
            const el = this.groupRef.current
            if (el) {
                el.style.cursor = this.props.isDraggable ? MOVE_CURSOR : DEFAULT_CURSOR
            }
        }
    }

    render() {
        const { randomProduct, collageLayoutsForm, showOverlap, isDraggable, form, isAlt, alternativeTemplatesLevels, levels, levelsMode } = this.props

        const templateSize = getTemplateSize()

        const screenWidth = templateSize.width
        const screenHeight = templateSize.height

        const collageLayout = form.is_collage && collageLayoutsForm && form.collage_layout_id && collageLayoutsForm[form.collage_layout_id] ? collageLayoutsForm[form.collage_layout_id] : (form.collage_layout || null)

        const byOverscan = !(form.max_image_width && form.max_image_height)
        const marginsX = byOverscan ? 2 * form.overscan : (form.max_image_width - screenWidth)
        const marginsY = byOverscan ? 2 * form.overscan : (form.max_image_height - screenHeight)

        const color = (form.enlarge && !form.is_collage) ? (marginsX < 0 ? OVERLAY_COLOR_NEGATIVE : OVERLAY_COLOR_POSITIVE) : undefined
        const pattern = (form.enlarge && !form.is_collage) ? (marginsY < 0 ? 'url(#overlap-pattern)' : 'url(#overlap-pattern-2)') : undefined

        return (
            <React.Fragment>
                <g ref={this.groupRef} className={isDraggable ? "draggable" : ""}>
                    <SourceImageSvgElements
                        isTemplatePreview={false}
                        isDraggable={isDraggable}
                        isAlt={isAlt}
                        levels={levels}
                        levelsMode={levelsMode}
                        randomProduct={randomProduct}
                        form={form}
                        collageLayout={collageLayout}
                        transX={this.state.x}
                        transY={this.state.y}
                        deltaAngle={this.props.deltaAngle}
                        setDeltaAngle={this.props.setDeltaAngle}
                        deltaPositions={this.props.deltaPositions}
                        setDeltaPositions={this.props.setDeltaPositions}
                    />
                </g>

                {(marginsX !== 0 || marginsY !== 0) && showOverlap && isDraggable &&
                    <g style={{ pointerEvents: 'none' }}>
                        {/* <rect
                            x={this.state.x - .25 * marginsX + form.translate_x}
                            y={this.state.y - .25 * marginsY + form.translate_y}
                            width={screenWidth + .5 * marginsX}
                            height={screenHeight + .5 * marginsY}
                            stroke={pattern}
                            fill={'transparent'}
                            strokeWidth={abs(.5 * marginsX)}
                        /> */}

                        <line
                            x1={this.state.x + form.translate_x - .25 * marginsX}
                            y1={this.state.y + form.translate_y - .25 * marginsY}
                            x2={this.state.x + form.translate_x + screenWidth + .25 * marginsX}
                            y2={this.state.y + form.translate_y - .25 * marginsY}
                            fill={'transparent'}
                            strokeLinecap={'square'}
                            strokeWidth={abs(.5 * marginsX)}
                            stroke={pattern}
                        />

                        <line
                            x1={this.state.x + form.translate_x - .25 * marginsX}
                            y1={this.state.y + form.translate_y + screenHeight + .25 * marginsY}
                            x2={this.state.x + form.translate_x + screenWidth + .25 * marginsX}
                            y2={this.state.y + form.translate_y + screenHeight + .25 * marginsY}
                            fill={'transparent'}
                            strokeLinecap={'square'}
                            strokeWidth={abs(.5 * marginsX)}
                            stroke={pattern}
                        />

                        <line
                            x1={this.state.x + form.translate_x - .25 * marginsX}
                            y1={this.state.y + form.translate_y + .25 * marginsY}
                            x2={this.state.x + form.translate_x - .25 * marginsX}
                            y2={this.state.y + form.translate_y + screenHeight - .25 * marginsY}
                            fill={'transparent'}
                            strokeLinecap={'square'}
                            strokeWidth={abs(.5 * marginsX)}
                            stroke={pattern}
                        />

                        <line
                            x1={this.state.x + form.translate_x + screenWidth + .25 * marginsX}
                            y1={this.state.y + form.translate_y + .25 * marginsY}
                            x2={this.state.x + form.translate_x + screenWidth + .25 * marginsX}
                            y2={this.state.y + form.translate_y + screenHeight - .25 * marginsY}
                            fill={'transparent'}
                            strokeLinecap={'square'}
                            strokeWidth={abs(.5 * marginsX)}
                            stroke={pattern}
                        />

                        <rect
                            x={this.state.x - .5 * marginsX + form.translate_x}
                            y={this.state.y - .5 * marginsY + form.translate_y}
                            width={screenWidth + marginsX}
                            height={screenHeight + marginsY}
                            stroke={color}
                            fill={'transparent'}
                            strokeWidth={'1'}
                        />

                        {marginsX > 0 &&
                            <rect
                                x={this.state.x + form.translate_x}
                                y={this.state.y + form.translate_y}
                                width={screenWidth}
                                height={screenHeight}
                                stroke={color}
                                fill={'transparent'}
                                strokeWidth={'1'}
                            />
                        }

                        {marginsX < 0 &&
                            <>
                            <line
                                x1={(this.state.x - .5 * marginsX + form.translate_x) / 2}
                                y1={0}
                                x2={(this.state.x - .5 * marginsX + form.translate_x) / 2}
                                y2={screenHeight}
                                strokeLinecap={"round"}
                                strokeWidth={`${this.state.x - .5 * marginsX + form.translate_x}`}
                                    stroke={pattern}
                                />
                                <line
                                    x1={screenWidth + (this.state.x + .5 * marginsX + form.translate_x) / 2}
                                    y1={0}
                                    x2={screenWidth + (this.state.x + .5 * marginsX + form.translate_x) / 2}
                                    y2={screenHeight}
                                    strokeLinecap={"round"}
                                    strokeWidth={`${-.5 * marginsX - this.state.x + form.translate_x}`}
                                    stroke={pattern}
                                />
                            </>
                        }

                        {marginsY < 0 &&
                            <>
                                <line
                                    x1={0}
                                    y1={(this.state.y - .5 * marginsY + form.translate_y) / 2}
                                    x2={screenHeight}
                                    y2={(this.state.y - .5 * marginsY + form.translate_y) / 2}
                                    strokeLinecap={"round"}
                                    strokeWidth={`${this.state.y - .5 * marginsY + form.translate_y}`}
                                    stroke={pattern}
                                />
                                <line
                                    x1={0}
                                    y1={screenWidth + (this.state.y + .5 * marginsY + form.translate_y) / 2}
                                    x2={screenHeight}
                                    y2={screenWidth + (this.state.y + .5 * marginsY + form.translate_y) / 2}
                                    strokeLinecap={"round"}
                                    strokeWidth={`${-.5 * marginsY - this.state.y + form.translate_y}`}
                                    stroke={pattern}
                                />
                            </>
                        }
                    </g>
                }
            </React.Fragment>
        )
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        alternativeTemplatesLevels: state.alternativeTemplatesLevels,
        form: selectFormValues(state, getFormName(state.isAlt)),
        randomProduct: state.randomProduct,
        collageLayoutsForm: selectFormValues(state,'collageLayoutsForm'),
        activeView: state.activeView,
        activeShapeId: state.activeShapeId,
    }
}

export default connect(mapStateToProps, {
    initForm,
    setActiveShapeId,
    setActiveView,
    setDragging,
})(SourceImageSvg)